import { useColorMode } from '@chakra-ui/react';
import { usePrevious } from '@gamma/ui/hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { LogScaleContext } from '../../providers';
import { useTenantInfo } from '../useTenantInfo/useTenantInfo';

/**
 * Dashboard details configuration
 */
interface DashboardDetails {
  name?: string;
  dashboardId?: string;
  investigatorApp?: string;
  corelightDashboard?: string;
}

/**
 * Hook that manages Humio UI iframe state and URL construction.
 * Handles iframe source URL generation based on tenant configuration and dashboard parameters.
 * Monitors iframe URL changes to keep context state synchronized.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.iframeType - Type of iframe ('search', 'dashboards', 'sensorsMonitoring')
 * @param {string} params.params - Optional query parameters for search iframe
 * @param {string} params.dashboardId - Dashboard ID for sensorsMonitoring iframe
 * @param {DashboardDetails} params.dashboardDetails - Dashboard configuration details
 * @returns {Object} Iframe reference and source URL
 */
export const useReloadHumioUI = ({
  iframeType,
  params,
  dashboardId,
  dashboardDetails,
}: {
  iframeType: string;
  params?: string;
  dashboardId?: string;
  dashboardDetails?: DashboardDetails;
}) => {
  const { humioUrlPrefix, repoSlug } = useTenantInfo();
  const {
    logScaleURL,
    setLogScaleURL,
    logScaleDashboardId,
    setLogScaleDashboardId,
    logScaleURLParams,
    setLogScaleURLParams,
    iframeKey,
    setIframeKey,
  } = useContext(LogScaleContext);

  const { colorMode } = useColorMode();
  const previousColorMode = usePrevious(colorMode);

  /**
   * Build base path using the normalized prefix + repo slug
   * - PRIME:    /search-betaprime/betaprime-logs/...
   * - POV:      /search-internal/<alias-or-id>-logs/...
   * - STANDARD: /search/<alias-or-id>-logs/...
   */
  const baseRepoPath = repoSlug ? `/${humioUrlPrefix}/${repoSlug}` : null;

  const base =
    baseRepoPath === null
      ? null // Return null if repoSlug is not available
      : iframeType === 'search'
        ? `${baseRepoPath}/search`
        : iframeType === 'dashboards'
          ? `${baseRepoPath}/dashboards${
              dashboardDetails?.corelightDashboard
                ? `/${dashboardDetails.corelightDashboard}`
                : ''
            }${
              dashboardDetails?.investigatorApp
                ? `/${dashboardDetails.investigatorApp}`
                : ''
            }${dashboardDetails?.name ? `/${dashboardDetails.name}` : ''}${
              dashboardDetails?.dashboardId
                ? `?dashboardId=${dashboardDetails.dashboardId}`
                : ''
            }`
          : iframeType === 'sensorsMonitoring'
            ? `${baseRepoPath}/dashboards/${dashboardId ?? ''}`
            : '';

  const [iframeSrc, setIframeSrc] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /**
   * Updates iframe source URL when dependencies change
   */
  useEffect(() => {
    if (base === null) {
      setIframeSrc('');
      return;
    }

    // Append params to /search only
    if (iframeType === 'search') {
      setIframeSrc(params ? `${base}?${params}` : `${base}`);
    } else {
      setIframeSrc(`${base}`);
    }
  }, [iframeType, params, base]);

  /**
   * Refreshes iframe when theme toggles
   */
  useEffect(() => {
    if (previousColorMode && colorMode !== previousColorMode) {
      setIframeKey && iframeKey !== undefined && setIframeKey(iframeKey + 1);
    }
  }, [colorMode, iframeKey, previousColorMode, setIframeKey]);

  /**
   * Polls iframe URL to keep context state synchronized with iframe navigation
   */
  useEffect(() => {
    const timer = setInterval(() => {
      const url = iframeRef?.current?.contentDocument?.URL;
      if (url) {
        const qs = decodeURIComponent(url)?.split('?')?.[1] ?? '';
        const parts = qs ? qs.split('&') : [];
        const nextDashboardId =
          parts[
            parts.findIndex((element) => element.includes('dashboardId'))
          ]?.split('=')?.[1];

        if (url !== logScaleURL && setLogScaleURL) {
          setLogScaleURL(url);
        }

        if (nextDashboardId !== logScaleDashboardId && setLogScaleDashboardId) {
          setLogScaleDashboardId(nextDashboardId);

          if (
            parts.sort().toString() !== logScaleURLParams?.sort()?.toString() &&
            setLogScaleURLParams
          ) {
            setLogScaleURLParams(parts);
          }
        }
      }
    }, 500);

    return () => clearInterval(timer);
  }, [
    logScaleURL,
    logScaleDashboardId,
    logScaleURLParams,
    setLogScaleDashboardId,
    setLogScaleURL,
    setLogScaleURLParams,
  ]);

  return {
    iframeRef,
    iframeSrc,
  };
};
